/**
 * Vault browsing, plus the actions that reach out of the Vault domain.
 *
 * The store owns tabs, filters, favourites and recents. This hook owns the parts
 * that touch other domains or the browser: writing a manifest file, the clipboard,
 * and loading a prompt or preset into the Create screen.
 *
 * SIMULATED. Pack contents are bundled fixtures and a "download" writes a text
 * manifest client-side; nothing is fetched. Access is gated on the client-side
 * `dfyVault` entitlement, which is advisory — the real endpoints must enforce it.
 */
import { useEffect, useMemo } from 'react';
import { apiErrorMessage } from '../api/errors';
import { buildVaultPackManifest } from '../lib/download';
import { downloadTextFile } from '../lib/download';
import { mapMixerGenreToAppGenre } from '../lib/music';
import { useCreateStore } from '../stores/createStore';
import { useUiStore } from '../stores/uiStore';
import { useVaultStore } from '../stores/vaultStore';
import { useVaultDataStore } from '../stores/vaultDataStore';
import type { VaultPack, VaultPreset, VaultPrompt } from '../types/domain';

/** Totals across the whole vault, for the summary cards. */
export interface VaultTotals {
  packs: number;
  prompts: number;
  presets: number;
  structures: number;
  hooks: number;
  metadata: number;
  marketing: number;
}

/**
 * Every asset count in one pass.
 *
 * Module-level and computed once: the packs are static fixtures, and the
 * prototype recomputed six separate `reduce` calls on every single render of the
 * Vault screen.
 */
function totalsFor(packs: VaultPack[]): VaultTotals {
  return packs.reduce<VaultTotals>(
    (acc, p) => ({
      packs: acc.packs + 1,
      prompts: acc.prompts + p.prompts.length,
      presets: acc.presets + p.presets.length,
      structures: acc.structures + p.structures.length,
      hooks: acc.hooks + p.hooks.length,
      metadata: acc.metadata + p.metadata.length,
      marketing: acc.marketing + p.marketing.length,
    }),
    { packs: 0, prompts: 0, presets: 0, structures: 0, hooks: 0, metadata: 0, marketing: 0 }
  );
}

/** Total asset count for one pack, as shown on its card. */
export function packAssetCount(p: VaultPack): number {
  return (
    p.prompts.length +
    p.presets.length +
    p.structures.length +
    p.hooks.length +
    p.metadata.length +
    p.marketing.length
  );
}

export function useVault() {
  const categories = useVaultDataStore((s) => s.categories);
  const packs = useVaultDataStore((s) => s.packs);
  const load = useVaultDataStore((s) => s.load);
  const loadVaultData = useVaultDataStore((s) => s.loadVaultData);

  const tab = useVaultStore((s) => s.tab);
  const search = useVaultStore((s) => s.search);
  const categoryFilter = useVaultStore((s) => s.categoryFilter);
  const openPackId = useVaultStore((s) => s.openPackId);
  const resourceTab = useVaultStore((s) => s.resourceTab);
  const favoritePacks = useVaultStore((s) => s.favoritePacks);
  const favoriteItems = useVaultStore((s) => s.favoriteItems);
  const downloads = useVaultStore((s) => s.downloads);
  const recentPacks = useVaultStore((s) => s.recentPacks);
  const recentPrompts = useVaultStore((s) => s.recentPrompts);
  const recentPresets = useVaultStore((s) => s.recentPresets);

  const setTab = useVaultStore((s) => s.setTab);
  const setSearch = useVaultStore((s) => s.setSearch);
  const setCategoryFilter = useVaultStore((s) => s.setCategoryFilter);
  const openPack = useVaultStore((s) => s.openPack);
  const closePack = useVaultStore((s) => s.closePack);
  const setResourceTab = useVaultStore((s) => s.setResourceTab);
  const togglePackFavorite = useVaultStore((s) => s.togglePackFavorite);
  const toggleItemFavorite = useVaultStore((s) => s.toggleItemFavorite);
  const recordDownload = useVaultStore((s) => s.recordDownload);
  const noteRecentPrompt = useVaultStore((s) => s.noteRecentPrompt);
  const noteRecentPreset = useVaultStore((s) => s.noteRecentPreset);
  const loadActivity = useVaultStore((s) => s.loadActivity);

  const loadIntoCreate = useCreateStore((s) => s.load);
  const showToast = useUiStore((s) => s.showToast);

  useEffect(() => {
    if (load.status === 'idle') {
      void Promise.all([loadVaultData().catch(() => undefined), loadActivity().catch(() => undefined)]);
    }
  }, [load.status, loadVaultData, loadActivity]);

  const openPack_ = useMemo(
    () => packs.find((p) => p.id === openPackId) ?? null,
    [packs, openPackId]
  );

  /** Packs matching the search box and the category chips. */
  const filteredPacks = useMemo(() => {
    const q = search.trim().toLowerCase();
    return packs.filter((p) => {
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.prompts.some(
          (x) => x.title.toLowerCase().includes(q) || x.text.toLowerCase().includes(q)
        ) ||
        p.hooks.some((x) => x.text.toLowerCase().includes(q)) ||
        p.metadata.some((x) => x.title.toLowerCase().includes(q));
      const matchesCategory = categoryFilter === 'All' || p.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [packs, search, categoryFilter]);

  /** Recently opened packs, resolved and in order. */
  const recentPackList = useMemo(
    () => recentPacks.flatMap((id) => packs.filter((p) => p.id === id)),
    [packs, recentPacks]
  );

  /** Favourited packs. */
  const favoritePackList = useMemo(
    () => packs.filter((p) => favoritePacks.includes(p.id)),
    [packs, favoritePacks]
  );

  /** Favourited individual items across every pack, each tagged with its pack. */
  const favoriteItemList = useMemo(
    () =>
      packs.flatMap((p) =>
        [...p.prompts, ...p.hooks, ...p.metadata, ...p.marketing]
          .filter((item) => favoriteItems.includes(item.id))
          .map((item) => ({
            id: item.id,
            packName: p.name,
            // Prompts, metadata and marketing have titles; hooks only have text.
            label: 'title' in item && item.title ? item.title : item.text,
          }))
      ),
    [packs, favoriteItems]
  );

  const totals = useMemo(() => totalsFor(packs), [packs]);

  return {
    tab,
    setTab,
    search,
    setSearch,
    categoryFilter,
    setCategoryFilter,
    openPack,
    closePack,
    setResourceTab,
    loadVaultData,
    resourceTab,
    /** The pack showing in the detail drawer, or null. */
    activePack: openPack_,
    favoritePacks,
    favoriteItems,
    togglePackFavorite,
    toggleItemFavorite,
    downloads,
    recentPrompts,
    recentPresets,
    load,
    categories,
    packs,
    totals,
    filteredPacks,
    recentPackList,
    favoritePackList,
    favoriteItemList,
    /** The pack shown in the featured hero. */
    heroPack: packs[0] ?? null,

    /** Writes the pack's manifest to a text file and logs the download. */
    downloadPack(pack: VaultPack): void {
      const { filename, contents } = buildVaultPackManifest(pack);
      downloadTextFile(filename, contents);
      void recordDownload(pack).catch((err) => {
        showToast(apiErrorMessage(err, { preserveMessage: false }));
      });
      showToast(`Downloading "${pack.name}"`);
    },

    copyText(text: string): void {
      void navigator.clipboard?.writeText(text);
      showToast('Copied to clipboard');
    },

    /**
     * Loads a vault prompt into the Create form.
     *
     * Named `apply*` rather than `use*`: the monolith's `useVaultPrompt` looked
     * like a React hook, so calling it from an onClick tripped the rules-of-hooks
     * lint. Behaviour is unchanged. The caller navigates to /create.
     */
    applyPrompt(pack: VaultPack, prompt: VaultPrompt): void {
      loadIntoCreate({ prompt: prompt.text, genre: mapMixerGenreToAppGenre(prompt.genre) });
      void noteRecentPrompt(pack.id, prompt.id, prompt.title);
      showToast(`"${prompt.title}" loaded into Create`);
    },

    /** Loads a genre preset into the Create form. The caller navigates to /create. */
    applyPreset(pack: VaultPack, preset: VaultPreset): void {
      const genreHint = pack.prompts[0]?.genre ?? pack.name;
      loadIntoCreate({
        prompt: `A ${preset.tempo.toLowerCase()}-tempo, ${preset.mood.toLowerCase()} track at ${preset.energy}% energy, structured as ${preset.structure.toLowerCase()}.`,
        genre: mapMixerGenreToAppGenre(genreHint),
        vocal: preset.vocals,
      });
      void noteRecentPreset(pack.id, preset.id, preset.name);
      showToast(`"${preset.name}" preset loaded into Create`);
    },
  };
}
